import { z } from 'zod';
import { TicketStatusSchema } from './ticket.schema';
import { SprintStatusSchema } from './sprint.schema';
import { CockpitStateSchema } from './cockpit.schema';
import { AchievementsStateSchema } from './achievements.schema';
import { CapstoneStateSchema } from './capstone.schema';
import { LocaleSchema } from './ui-prefs.schema';

/**
 * Access tier · cohort/tariff model (slot_role_management · 10 JUL 2026 · Genie).
 * Each tier is a cap on the highest sprint a user may receive content for — the
 * cap table + all helpers live in `shared/access/roles.ts` (SINGLE SOURCE OF
 * TRUTH). Keys here MUST match ROLE_TIERS there AND the DB `profiles_role_check`.
 *
 *   free       — S01 only (лид-магнит · не оплачен).
 *   trial      — S01–S04 (демо-раунд · не оплачен · S05 peekable read-only).
 *   start      — S01–S07 (оплачен).
 *   manualplus — S01–S13 (оплачен).
 *   pro        — S01–S19 (оплачен · весь текущий курс).
 *   admin      — всё + DEV unlock-all.
 *
 * Legacy `'full'` (pre-10-JUL) is migrated to `'pro'` in the DB and coerced by
 * `coerceRole()`; it is intentionally NOT a valid enum member anymore.
 * Mirrors public.profiles.role. Optional FE-side: absent ⇒ treat as 'trial'
 * (least privilege) via `roleOf()` — keeps pre-existing User fixtures valid.
 */
export const UserRoleSchema = z.enum(['free', 'trial', 'start', 'manualplus', 'pro', 'admin']);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  // FE-side optional (legacy pre-Phase-3 shape) · server enforces NOT NULL via DB
  // schema per ADR-004. New users always have email; pre-auth fixtures may omit.
  email: z.string().email().optional(),
  locale: LocaleSchema,
  timezone: z.string(),
  createdAt: z.string(),
  role: UserRoleSchema.optional(),
});

export const SprintProgressSchema = z.object({
  status: SprintStatusSchema,
  pctDone: z.number(),
  finalTestScore: z.number().optional(),
  capstonePassed: z.boolean().optional(),
  reflection: z.string().optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
});

export const QuickCheckProgressSchema = z.object({
  state: z.enum(['pristine', 'correct', 'wrong']),
  selectedOption: z.string().optional(),
  revealedAt: z.string().optional(),
});

export const WizardBlockStateSchema = z.object({
  activeStep: z.number().int(),
});

export const InlineQcStateSchema = z.object({
  state: z.enum(['pristine', 'correct', 'wrong']),
  selectedOption: z.string().optional(),
  revealedAt: z.string().optional(),
});

export const TicketProgressSchema = z.object({
  status: TicketStatusSchema,
  acChecked: z.record(z.string(), z.boolean()),
  quickCheck: QuickCheckProgressSchema.optional(),
  wizardState: z.record(z.string(), WizardBlockStateSchema).optional(),
  inlineQc: z.record(z.string(), InlineQcStateSchema).optional(),
  note: z.string().optional(),
  missionDone: z.boolean().optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  // CHAT_2B_PENDING_QUESTIONS Phase 1 batch · 16 MAY (Genie pick all=adopt).
  // Powers Board.tsx blocked-chip primitive (Slot C 16 MAY placeholder trigger).
  // Max 200 chars per recommendation · prevents Board card overflow.
  blockedReason: z.string().max(200).optional(),
  // CHAT_2B_PENDING_QUESTIONS Q-4 · per-status transition timestamp (simpler
  // single-timestamp variant per brief recommendation · accurate age-in-column
  // для current column). UI consumer: Board.tsx `getColumnAge(ticket)` ·
  // currently uses `ticket.startedAt` proxy. Set on each status mutation
  // (in ProgressContext setTicketStatus).
  columnChangedAt: z.string().optional(),
  // CHAT_2B_PENDING_QUESTIONS Q-5 · multi-assignee UUID array. Powers
  // АК/МС/ДС filter UI в Board.tsx (Block 2+ collab scenarios). Per ADR-004 ·
  // assignees[0] = primary for notification routing. No backward-compat
  // conversion needed · field had no predecessor `assignee: string`.
  assignees: z.array(z.string()).optional(),
  // CHAT_2B_PENDING_QUESTIONS Q-6 · auto-updated on ANY ticket mutation
  // (status · ac toggle · note · blockedReason · etc.). Powers «обновлён 14 мин
  // назад» meta в Ticket.tsx + Board.tsx card headers. Per ADR-007 sync ·
  // clients use для If-Unmodified-Since conflict detection. Bumped in
  // ProgressContext on each ticket-touching setter.
  updatedAt: z.string().optional(),
});

export const HomeworkStatusSchema = z.enum(['locked', 'in-progress', 'done']);

export const HomeworkProgressSchema = z.object({
  status: HomeworkStatusSchema,
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  /**
   * Homework v2 (slot_homework_rework): self-check checklist state · idx → checked.
   * Gates the «Сдать» button alongside `submission`. Optional — absent for v1/old saves.
   */
  selfCheck: z.record(z.string(), z.boolean()).optional(),
  /**
   * Homework v2: the learner's submitted artefact (text or link) · portfolio data,
   * synced to the backend. Optional — absent for v1/old saves.
   */
  submission: z.string().optional(),
  /**
   * Homework v2 structured surfaces (Story / AC list / MoSCoW / bug-report):
   * the surface's structured value, used to re-hydrate the working surface on
   * re-open. Human-readable form is mirrored into `submission`. Optional —
   * absent for free-text homeworks and v1/old saves.
   */
  submissionData: z.record(z.string(), z.unknown()).optional(),
});

export const SkillProgressSchema = z.object({
  pct: z.number(),
  ticketsContributed: z.array(z.string()),
});

export const OnboardingFlagsSchema = z.object({
  sprint0TourCompleted: z.boolean(),
  dashboardSeen: z.boolean(),
  themeSwitcherTried: z.boolean(),
  welcomeSeen: z.boolean(),
});

export const PomodoroModeSchema = z.enum(['work', 'short-break', 'long-break', 'idle']);

/**
 * PomodoroState · `mode` + `startedAt` + `durationSec` synced via Progress JSONB.
 * `remainingSec` and `sessionsToday` are per-device only per OAQ-2a-8 = B; the
 * `/api/progress` PATCH handler strips these before persisting. Schema retains
 * them for FE compatibility (ProgressContext writes the full shape locally).
 */
export const PomodoroStateSchema = z.object({
  mode: PomodoroModeSchema,
  startedAt: z.string().optional(),
  durationSec: z.number().int(),
  remainingSec: z.number().int().optional(),
  sessionsToday: z.number().int().optional(),
});

/**
 * Per-sprint quiz review state · Slot QUIZ_AGGREGATION_WIDGET (2026-05-12).
 * Powers Hybrid gate state machine in QuizAggregationWidget:
 *   - attempts:           counted retakes · widget initializes to 0 on first submit
 *   - lastAttemptAt:      ISO timestamp · drives 24h cooldown gate
 *   - reviewUnlockedAt:   ISO timestamp · sticky once set (passed OR forced-unlock after 2 attempts)
 *   - bestScore:          0-100 pct · drives passed gate (≥80%) · tracks max across attempts
 *
 * Keys are bank ids (`S01`-`S18` for sprint banks · `ctfl-mock-1` / `ctfl-mock-2`
 * for mocks if OQ-3 mock-gate is enabled). OQ-3=C means mocks bypass the gate
 * entirely · their key entries may stay undefined indefinitely (Tier 3 reveals
 * immediately on submit · no attempt/cooldown tracking needed for mocks).
 *
 * Per OQ-5c=A the existing `QuizAttemptStateSchema` in `quiz.schema.ts` stays
 * stand-alone (attempt-in-flight concern · separate from review-unlock concern).
 *
 * Note: no Zod `.default()` here · INPUT/OUTPUT types kept structurally identical
 * to avoid `apiFetch<T>(z.ZodType<T>, ...)` invariance mismatch (input-vs-output
 * inference splits Progress identity when defaults expand fields). Widget code
 * initializes fresh entries explicitly · consumers use `progress.quiz?.[id] ?? init`.
 */
export const QuizProgressSchema = z.object({
  attempts: z.number().int().nonnegative(),
  lastAttemptAt: z.string().optional(),
  reviewUnlockedAt: z.string().optional(),
  bestScore: z.number().min(0).max(100),
});

/**
 * CHAT_2B_PENDING_QUESTIONS Q-2 · Rich event stream (Genie adopt 16 MAY).
 *
 * Backed event log powering richer «📔 Журнал недели» feed в Sprint.tsx (currently
 * только last 3 ticket completions render) и Progress.tsx activity-weeks data
 * source (per CF-D-1 backend endpoint graceful-degrade).
 *
 * Known `kind` values (open-ended · extensible):
 *   - `ticket-status-change` · payload `{ sprintId · ticketId · prevStatus · nextStatus }`
 *   - `comment-added`        · payload `{ sprintId · ticketId · commentLength }`
 *   - `time-spent`           · payload `{ sprintId · ticketId · fromStatus · durationMin }`
 *   - `badge-unlocked`       · payload `{ badgeId · sprintId? }`
 *
 * `payload` shape is per-kind · enforced by consumers via discriminator type
 * narrowing (not Zod discriminated union · keeps schema extensible without
 * lock-step monorepo updates per new kind). Set `kind` to a known value or
 * coordinate via cross-flag if adding new kinds.
 *
 * Pruning: per ADR-009 telemetry retention pattern · 90-day window matching
 * pomodoroSessions retention. Inline prune-on-write in ProgressContext
 * `addProgressEvent` setter (drops events older than 90d at append time).
 * Cross-cut с `shared/utils/telemetry-prune.ts` deferred к explicit run if
 * inline pruning insufficient.
 */
export const ProgressEventSchema = z.object({
  id: z.string(),
  at: z.string(),
  kind: z.string(),
  payload: z.record(z.string(), z.unknown()),
});

export const ProgressLastSchema = z.object({
  sprintId: z.string(),
  ticketId: z.string().optional(),
  subview: z.enum(['tickets', 'backlog', 'review']).optional(),
  timestamp: z.string(),
});

export const ProgressSchema = z.object({
  last: ProgressLastSchema,
  sprints: z.record(z.string(), SprintProgressSchema),
  tickets: z.record(z.string(), TicketProgressSchema),
  homework: z.record(z.string(), HomeworkProgressSchema),
  // Slot QUIZ_AGGREGATION_WIDGET extension 2026-05-12 · per-sprint quiz review
  // state for Hybrid gate (attempts · cooldown · sticky reviewUnlockedAt ·
  // bestScore). Keys are bank ids (`S01` etc). Optional · old localStorage data
  // parses without this field (zero migration cost · per
  // `feedback_qa_studio_no_content_loss`). Consumers use `progress.quiz?.[id]`.
  quiz: z.record(z.string(), QuizProgressSchema).optional(),
  // CHAT_2B Q-2 · rich event stream (Genie adopt 16 MAY) · powers «📔 Журнал
  // недели» feed + Progress.tsx activity-weeks source · 90d prune-on-write
  // pattern (ADR-009). Optional · consumers use `progress.events ?? []`.
  events: z.array(ProgressEventSchema).optional(),
  skills: z.record(z.string(), SkillProgressSchema),
  onboarding: OnboardingFlagsSchema,
  pomodoro: PomodoroStateSchema.optional(),
  cockpit: CockpitStateSchema,
  achievements: AchievementsStateSchema,
  capstone: z.object({
    'block-1': CapstoneStateSchema.optional(),
    // W3.1 extension 2026-05-04 · per-block capstone state for Block 2/3/4
    // releases (v1.0 · v1.5 · v2.0). Optional · undefined until earned.
    'block-2': CapstoneStateSchema.optional(),
    'block-3': CapstoneStateSchema.optional(),
    'block-4': CapstoneStateSchema.optional(),
  }),
  /**
   * Legacy field from pre-foreman data model · removed from current frontend types
   * (no setter in ProgressContext · no UI). Retained as `unknown` per OAQ-2a-7 = B
   * so localStorage migration imports do not silently strip it. Server never serves.
   */
  streak: z.unknown().optional(),
});

/**
 * Per-branch metadata stamps · per ADR-007 (hybrid timestamps).
 * `lastUpdatedAt` mirrors `progress.updated_at` for ETag computation.
 * `branches` map carries per-branch ISO timestamps · server bumps the entry
 * when a PATCH touches the corresponding branch. Clients send the prior value
 * back in `If-Unmodified-Since` for conflict detection (412 + 1 retry).
 *
 * Branch keys: `last` · `sprints` · `tickets` · `homework` · `skills` ·
 * `onboarding` · `pomodoro` · `cockpit` · `achievements` · `capstone`.
 */
export const ProgressMetaSchema = z.object({
  lastUpdatedAt: z.string(),
  branches: z.record(z.string(), z.string()).default({}),
});

/**
 * ProgressWithMeta · DB JSONB shape per ADR-007 (hybrid timestamps · single row).
 * Exposed only at server boundaries (DB ↔ PATCH handler). The client-facing API
 * returns `Progress` with `_meta` projected into `Last-Modified` / `ETag` headers.
 */
export const ProgressWithMetaSchema = ProgressSchema.extend({
  _meta: ProgressMetaSchema,
});

export type User = z.infer<typeof UserSchema>;
export type SprintProgress = z.infer<typeof SprintProgressSchema>;
export type QuickCheckProgress = z.infer<typeof QuickCheckProgressSchema>;
export type WizardBlockState = z.infer<typeof WizardBlockStateSchema>;
export type InlineQcState = z.infer<typeof InlineQcStateSchema>;
export type TicketProgress = z.infer<typeof TicketProgressSchema>;
export type HomeworkStatus = z.infer<typeof HomeworkStatusSchema>;
export type HomeworkProgress = z.infer<typeof HomeworkProgressSchema>;
export type QuizProgress = z.infer<typeof QuizProgressSchema>;
export type ProgressEvent = z.infer<typeof ProgressEventSchema>;
export type SkillProgress = z.infer<typeof SkillProgressSchema>;
export type OnboardingFlags = z.infer<typeof OnboardingFlagsSchema>;
export type PomodoroMode = z.infer<typeof PomodoroModeSchema>;
export type PomodoroState = z.infer<typeof PomodoroStateSchema>;
export type ProgressLast = z.infer<typeof ProgressLastSchema>;
export type Progress = z.infer<typeof ProgressSchema>;
export type ProgressMeta = z.infer<typeof ProgressMetaSchema>;
export type ProgressWithMeta = z.infer<typeof ProgressWithMetaSchema>;
